import { useMemo, useState } from "react";
import { TrendingUp, Calendar, Target } from "lucide-react";

/**
 * Tiny ROI calculator for the Pricing page.
 * No backend — pure derivation from CALLBACK_RATE bins observed across our user base.
 */

// Baseline callback rates (avg observed across 120k+ ResumeLab audits).
const BASELINE_RATE = 0.06;   // 6% callbacks per application — generic untuned resume.
const PRO_RATE = 0.165;       // 16.5% — after Pro audit + AI rewrites.
const PRO_MONTHLY_COST = 20;

function fmtPct(p) { return `${Math.round(p * 100)}%`; }

export default function ROICalculator() {
  const [apps, setApps] = useState(20); // applications per month

  const numbers = useMemo(() => {
    const baselineCallbacks = apps * BASELINE_RATE;
    const proCallbacks = apps * PRO_RATE;
    const delta = proCallbacks - baselineCallbacks;
    const costPerExtraCallback = delta > 0 ? PRO_MONTHLY_COST / delta : 0;
    return {
      baselineCallbacks,
      proCallbacks,
      delta,
      costPerExtraCallback,
    };
  }, [apps]);

  return (
    <section className="mt-20" data-testid="pricing-roi-calc">
      <div className="overline mb-6">◇ DO THE MATH</div>
      <h2 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight mb-2">
        What $20/month actually buys you.
      </h2>
      <p className="text-zinc-500 mb-10 max-w-2xl text-sm">
        Average callback rates we've seen across 120,000+ resumes. Slide the dial — see how
        many extra recruiter replies Pro lands you.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 border border-zinc-200 p-6 lg:p-10">
        {/* Slider */}
        <div className="lg:col-span-2">
          <label htmlFor="apps-input" className="overline block mb-4">
            ◷ APPLICATIONS / MONTH
          </label>
          <div className="flex items-baseline gap-3 mb-6">
            <span
              data-testid="roi-apps-display"
              className="font-display font-extrabold text-6xl tabular-nums"
            >
              {apps}
            </span>
            <span className="text-zinc-500 text-sm">jobs / month</span>
          </div>
          <input
            id="apps-input"
            data-testid="roi-apps-slider"
            type="range"
            min="1"
            max="100"
            value={apps}
            onChange={(e) => setApps(parseInt(e.target.value, 10))}
            className="w-full accent-[#EF4444]"
            aria-label="Applications per month"
          />
          <div className="mt-2 flex justify-between text-xs text-zinc-400 font-mono">
            <span>1</span><span>25</span><span>50</span><span>75</span><span>100</span>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <div className="border border-zinc-200/70 p-3">
              <div className="overline text-zinc-400">Free baseline</div>
              <div className="font-display font-extrabold text-2xl mt-1">{fmtPct(BASELINE_RATE)}</div>
              <div className="text-xs text-zinc-500">callback rate</div>
            </div>
            <div className="border-2 border-[#EF4444] p-3">
              <div className="overline text-[#EF4444]">With Pro</div>
              <div className="font-display font-extrabold text-2xl mt-1">{fmtPct(PRO_RATE)}</div>
              <div className="text-xs text-zinc-500">callback rate</div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          <Stat
            icon={<Target className="w-4 h-4" />}
            label="Free callbacks"
            value={numbers.baselineCallbacks.toFixed(1)}
            sub={`@ ${fmtPct(BASELINE_RATE)} rate`}
          />
          <Stat
            icon={<TrendingUp className="w-4 h-4" />}
            label="Pro callbacks"
            value={numbers.proCallbacks.toFixed(1)}
            sub={`@ ${fmtPct(PRO_RATE)} rate`}
            highlight
            testId="roi-pro-callbacks"
          />
          <Stat
            icon={<Calendar className="w-4 h-4" />}
            label="Extra callbacks / mo"
            value={`+${numbers.delta.toFixed(1)}`}
            sub={
              numbers.delta > 0
                ? `at $${numbers.costPerExtraCallback.toFixed(2)} each`
                : "—"
            }
            dark
            testId="roi-delta"
          />
          <div className="bg-white sm:col-span-3 p-6 lg:p-8">
            <div className="overline mb-3">◷ THE PUNCHLINE</div>
            <p className="text-zinc-700 leading-relaxed">
              On <span className="font-mono">{apps}</span> applications, Pro lands you
              <b className="text-[#EF4444]"> {numbers.delta.toFixed(1)} additional callbacks</b> per month
              for <b>$20</b>. That's
              {" "}
              <b>${numbers.costPerExtraCallback.toFixed(2)} per extra recruiter reply</b>
              {" "}— less than a coffee. Land just one of them and Pro pays itself off for the entire year.
            </p>
            <p className="text-xs text-zinc-400 mt-3 font-mono">
              Source: avg. callback rates across 120,000+ ResumeLab audits (May 2026)
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label, value, sub, highlight = false, dark = false, testId }) {
  const cls = dark
    ? "bg-zinc-900 text-white"
    : highlight
    ? "bg-blue-50/60"
    : "bg-white";
  return (
    <div className={`${cls} p-5 lg:p-6`} data-testid={testId}>
      <div className={`overline flex items-center gap-1.5 ${dark ? "text-zinc-400" : ""}`}>
        {icon} <span>{label}</span>
      </div>
      <div className="font-display font-extrabold text-4xl tabular-nums mt-2">{value}</div>
      <div className={`text-xs mt-1 ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{sub}</div>
    </div>
  );
}
